// Service photos, one set per dialog: a work's own (the yard's, up to five, from the work's camera button) or a
// vessel's from the client (up to five the client sent, from Client photos in the vessel's header: they are of
// the boat, not of one work). Each has a note and is saved in Smartsheet itself (one comment per photo on the
// row, "📷 <note>" or the client's "📩 From the client: <note>", with the image attached), so nothing lands
// in the app's database.
// A Modal (a bottom sheet on phones) portalled to <body>, because its trigger sits inside a table.
//   - the count ("2 of 5"), then a card per photo: thumbnail (opens it full size in-app), the note (saves
//     on blur when it changed), who added it (the person in Rolodex, not the app's Smartsheet account) and
//     when, Delete (asks first).
//   - Take photo (rear camera, touch only) and Choose photos (several at once). Photos are shrunk in the
//     browser first (lib/shrinkImage.ts); a HEIC (an iPhone's) is made a JPEG there, so every browser shows it.
//     A placeholder card shows while one uploads ("Photo 2 of 3").
//   - Drag and drop: photos dragged from the computer can be dropped anywhere on the dialog (it lights up
//     and says how many more fit); with a mouse a drop area under the photos says so and opens the chooser
//     when clicked. Photos dropped on a work's camera button (or a vessel's Client photos) open the dialog and go straight up
//     (`initialFiles`). Past the five, or a file that isn't a photo, is left out and the dialog says so.
//   - A HEIC already saved (added before, or straight in Smartsheet) that the browser can't show is drawn
//     from the file by lib/heic.ts, its thumbnail and its full view both.
//   - read-only (no service:write): photos and notes, nothing to add, edit or delete. A photo attached
//     straight to the row in Smartsheet has no comment, so its note can't be written from here.
import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SERVICE_PHOTO_MAX, SERVICE_PHOTO_NOTE_MAX, type ServicePhotoSource } from '@technohull/shared';
import {
  servicePhotoUrl,
  useDeleteServicePhoto,
  useEditServicePhotoNote,
  useServicePhotos,
  useUploadServicePhoto,
  type ServicePhoto,
} from '../features/servicePhotos.js';
import { useDateFormatter } from '../lib/format.js';
import { useNameResolver } from '../features/directory.js';
import { useIsCoarse } from '../lib/media.js';
import { heicPreviewUrl, isHeicName, isHeicType } from '../lib/heic.js';
import { imageTypeOf, readAsDataUrl, shrinkImage } from '../lib/shrinkImage.js';
import { InlineError, InlineLoading, LogoSpin, Modal, useConfirm } from './ui.js';
import { IconCamera, IconTrash, IconUpload } from './icons.js';

const GREEK = /[Ͱ-Ͽἀ-῿]/;
const lang = (s: string) => (GREEK.test(s) ? 'el' : undefined);
const short = (s: string, n = 60) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const messageOf = (e: unknown) => (e instanceof Error && e.message ? e.message : 'Something went wrong');
/** what the file pickers offer: any image, and HEIC by name too (Windows lists .heic only when asked) */
export const PHOTO_ACCEPT = 'image/*,.heic,.heif';
/** a file that could be a photo (shrinkImage decides the rest, with a message) */
const isPhotoFile = (f: File) => imageTypeOf(f).startsWith('image/') || isHeicName(f.name);
const isHeicPhoto = (p: Pick<ServicePhoto, 'mimeType' | 'name'>) => isHeicType(p.mimeType) || isHeicName(p.name);
/** whether a drag carries files (not text or a link being dragged about the page) */
export const dragHasFiles = (dt: DataTransfer | null) => !!dt && Array.from(dt.types ?? []).includes('Files');

/** which of the files dropped or picked are added, given the room left: the photos that fit, in order,
 *  and how many were left out for want of room or for not being photos. */
export function photosToAdd(files: readonly File[], room: number): { take: File[]; overMax: number; notPhotos: number } {
  const photos = files.filter(isPhotoFile);
  const take = photos.slice(0, Math.max(0, room));
  return { take, overMax: photos.length - take.length, notPhotos: files.length - photos.length };
}

export function ServicePhotos({ rowId, name, source = 'yard', canWrite, onClose, initialFiles }: {
  rowId: number;
  /** the work's name, or the vessel's for the client's photos */
  name: string;
  /** the yard's photos of a work, or the client's of a vessel */
  source?: ServicePhotoSource;
  canWrite: boolean;
  onClose: () => void;
  /** photos dropped on the button that opened it: added as soon as the dialog has the row's photos */
  initialFiles?: File[];
}) {
  const q = useServicePhotos(rowId);
  const upload = useUploadServicePhoto();
  const del = useDeleteServicePhoto();
  const coarse = useIsCoarse();
  const df = useDateFormatter();
  const nameOf = useNameResolver();
  // who added it: the person in Rolodex when it was added there (Smartsheet names the app's own account)
  const who = (p: ServicePhoto) => (p.byEmail ? nameOf(p.byEmail) : p.by);
  const [confirmNode, confirm] = useConfirm();
  const [viewing, setViewing] = useState<number | null>(null);
  const [pending, setPending] = useState<{ stage: 'prep' | 'send'; preview?: string; heic?: boolean; n: number; of: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // a deleted card takes its focused button with it, and Add is disabled while it uploads: either way the
  // focus would drop to <body>, where Esc no longer reaches the dialog. Bring it back inside.
  const refocus = () => {
    const a = document.activeElement;
    if (!a || a === document.body || !a.isConnected) rootRef.current?.focus({ preventScroll: true });
  };

  // the row's photos of this dialog's set (a row's others, if any, are left to their own dialog)
  const photos = (q.data?.photos ?? []).filter((p) => (p.source ?? 'yard') === source);
  const fromClient = source === 'client';
  const max = q.data?.max ?? SERVICE_PHOTO_MAX;
  const full = photos.length >= max;
  const maxText = fromClient ? `This vessel has the maximum of ${max} photos from the client` : `This work has the maximum of ${max} photos`;
  const canAdd = canWrite && !!q.data && !full && !pending;
  const room = Math.max(0, max - photos.length);
  const shown = viewing != null ? photos.findIndex((p) => p.id === viewing) : -1;
  const viewed = shown >= 0 ? photos[shown]! : null;

  // photos go up one after another; files dropped while some are going up wait their turn
  const running = useRef(false);
  const later = useRef<File[][]>([]);
  const latest = useRef({ count: photos.length, max, ready: !!q.data });
  latest.current = { count: photos.length, max, ready: !!q.data };
  const addAll = async (files: File[]) => {
    if (!canWrite || !files.length) return;
    if (running.current) { later.current.push(files); return; }
    running.current = true;
    setError(null);
    const problems: string[] = [];
    try {
      let job: File[] | undefined = files;
      while (job) {
        const start = latest.current.count;
        const { take, overMax, notPhotos } = photosToAdd(job, latest.current.max - start);
        let added = 0;
        for (let i = 0; i < take.length; i++) {
          const file = take[i]!;
          setPending({ stage: 'prep', heic: isHeicType(imageTypeOf(file)) || isHeicName(file.name), n: i + 1, of: take.length });
          try {
            const photo = await shrinkImage(file);
            // a preview for the placeholder card, from the JPEG the browser just made (data: is CSP-safe)
            const preview = photo.shrunk ? await readAsDataUrl(photo.blob).catch(() => undefined) : undefined;
            setPending({ stage: 'send', preview, n: i + 1, of: take.length });
            await upload.mutateAsync({ rowId, file: photo.blob, name: photo.name, type: photo.type, source });
            added++;
          } catch (e) {
            problems.push(take.length > 1 ? `${file.name || `Photo ${i + 1}`}: ${messageOf(e)}` : messageOf(e));
          }
        }
        // the count catches up on a render (one may already have, during the batch); until then this batch
        // keeps its own, counted once
        latest.current = { ...latest.current, count: Math.max(latest.current.count, start + added) };
        if (overMax) problems.push(`${plural(overMax, 'photo')} left out: ${fromClient ? 'a vessel takes up to' : 'a work takes up to'} ${latest.current.max} photos${fromClient ? ' from the client' : ''}.`);
        if (notPhotos) problems.push(`${plural(notPhotos, 'file')} left out: only photos can be added.`);
        job = later.current.shift();
      }
    } finally {
      running.current = false;
      setPending(null);
      if (problems.length) setError(new Error(problems.join(' ')));
      refocus();
    }
  };
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void addAll(files);
  };
  const addRef = useRef(addAll);
  addRef.current = addAll;

  // photos dropped on the button that opened it go up once the dialog knows how many the row has
  const startedWith = useRef(false);
  useEffect(() => {
    if (startedWith.current || !q.data || !initialFiles?.length) return;
    startedWith.current = true;
    void addRef.current(initialFiles);
  }, [q.data, initialFiles]);

  // while the dialog is open, photos dragged from the computer can be dropped anywhere on it (and a file
  // dropped beside it isn't opened by the browser in place of the app). There's no reliable "drag left
  // the window" event, so the highlight goes once the drag-overs stop.
  useEffect(() => {
    if (!canWrite) return;
    let quiet: ReturnType<typeof setTimeout> | undefined;
    // (a drop another part of the page took, the chat panel say, is left to it)
    const over = (e: DragEvent) => {
      if (e.defaultPrevented || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      const l = latest.current;
      if (e.dataTransfer) e.dataTransfer.dropEffect = l.ready && l.count < l.max ? 'copy' : 'none';
      setDragging(true);
      clearTimeout(quiet);
      quiet = setTimeout(() => setDragging(false), 250);
    };
    const drop = (e: DragEvent) => {
      if (e.defaultPrevented || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      clearTimeout(quiet);
      setDragging(false);
      if (latest.current.ready) void addRef.current(Array.from(e.dataTransfer?.files ?? []));
    };
    window.addEventListener('dragenter', over);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      clearTimeout(quiet);
      window.removeEventListener('dragenter', over);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, [canWrite]);
  const remove = async (p: ServicePhoto) => {
    const ok = await confirm({
      title: 'Delete photo',
      message: `Delete this ${fromClient ? 'photo from the client' : 'photo'}${p.note ? ` (“${short(p.note)}”)` : ''} from “${name}”? It is removed from Smartsheet too and can’t be restored.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setError(null);
    setViewing(null);
    del.mutate({ rowId, id: p.id }, { onError: (e) => setError(e), onSettled: () => setTimeout(refocus, 0) });
  };
  const deleting = (p: ServicePhoto) => del.isPending && del.variables?.id === p.id;
  const when = (p: ServicePhoto) => (p.at ? df.dateTime(p.at, { year: 'auto' }) : '');

  return createPortal(
    <Modal
      title={fromClient ? 'Photos from the client' : 'Photos'}
      onClose={onClose}
      closeLabel="Close photos"
      width={680}
      className="svc-ph-modal"
      footer={
        <>
          {canWrite && full && <span className="svc-ph-max">{maxText}</span>}
          <button type="button" className="btn btn-ghost svc-ph-done" onClick={onClose}>Close</button>
          {canWrite && (
            <>
              <input ref={camRef} className="svc-ph-file" type="file" accept="image/*" capture="environment" onChange={pick} tabIndex={-1} aria-hidden="true" />
              <input ref={fileRef} className="svc-ph-file" type="file" accept={PHOTO_ACCEPT} multiple onChange={pick} tabIndex={-1} aria-hidden="true" />
              {coarse && (
                <button type="button" className="btn btn-primary" disabled={!canAdd} onClick={() => camRef.current?.click()}>
                  <IconCamera size={16} /> Take photo
                </button>
              )}
              <button type="button" className={`btn ${coarse ? '' : 'btn-primary'}`} disabled={!canAdd} onClick={() => fileRef.current?.click()}>
                <IconUpload size={16} /> Choose photos
              </button>
            </>
          )}
        </>
      }
    >
      <div className={`svc-ph${dragging ? ' is-drag' : ''}`} ref={rootRef} tabIndex={-1}>
        {dragging && canWrite && (
          <div className={`svc-ph-dropover${full ? ' is-full' : ''}`} aria-hidden="true">
            <IconUpload size={28} />
            <span>{full ? maxText : `Drop ${fromClient ? 'the client’s photos' : 'the photos'} to add them (${room} more fit)`}</span>
          </div>
        )}
        <div className="svc-ph-head">
          <span className="svc-ph-work" lang={lang(name)}>{name}</span>
          {q.data && (
            <span className="svc-ph-count" aria-label={`${photos.length} of ${max} ${fromClient ? 'photos from the client' : 'photos'}`}>
              <b>{photos.length}</b> of {max}
            </span>
          )}
        </div>
        {q.isLoading && <InlineLoading label="Loading photos…" />}
        {q.error && <InlineError error={q.error} />}
        {error != null && <InlineError error={error} onDismiss={() => setError(null)} />}
        {q.data && photos.length === 0 && !pending && (
          <p className="muted svc-ph-msg">
            {fromClient
              ? (canWrite ? `No photos from the client yet. Add the ones they sent (by email or WhatsApp, say), up to ${max}; they are saved in Smartsheet, in this vessel’s comments, marked “From the client”.` : 'No photos from the client yet.')
              : (canWrite ? `No photos yet. Add up to ${max}; they are saved in Smartsheet, in this work’s comments.` : 'No photos yet.')}
          </p>
        )}
        {(photos.length > 0 || pending) && (
          <ul className="svc-ph-grid">
            {photos.map((p, i) => (
              <li key={p.id} className={`svc-ph-card${deleting(p) ? ' is-busy' : ''}`}>
                <button type="button" className="svc-ph-thumb" onClick={() => setViewing(p.id)} aria-label={`Open photo ${i + 1}${p.note ? `: ${short(p.note)}` : ''}`}>
                  <PhotoImage
                    src={servicePhotoUrl(rowId, p.id)}
                    heic={isHeicPhoto(p)}
                    maxEdge={480}
                    alt=""
                    lazy
                    fallback={(busy) => <span className="svc-ph-nopv">{busy ? <LogoSpin size={24} decorative /> : <IconCamera size={22} />}<span>{busy ? 'Opening HEIC…' : 'No preview'}</span></span>}
                  />
                </button>
                <div className="svc-ph-body">
                  {canWrite && p.commentId != null ? (
                    <NoteField rowId={rowId} photo={p} label={`Note for photo ${i + 1}`} />
                  ) : p.note ? (
                    <p className="svc-ph-note" lang={lang(p.note)}>{p.note}</p>
                  ) : (
                    <p className="svc-ph-note is-empty">{p.commentId == null ? 'Added in Smartsheet, no note' : 'No note'}</p>
                  )}
                  <div className="svc-ph-meta">
                    <span className="svc-ph-who">
                      <span className="svc-ph-by" lang={lang(who(p))}>{who(p) || 'Smartsheet'}</span>
                      {p.at && <span className="svc-ph-at">{when(p)}</span>}
                    </span>
                    {canWrite && (
                      <button type="button" className="btn btn-sm btn-ghost btn-del svc-ph-del" disabled={deleting(p)} onClick={() => void remove(p)} aria-label={`Delete photo ${i + 1}`}>
                        <IconTrash size={14} /> {deleting(p) ? 'Deleting…' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {pending && (
              <li className="svc-ph-card is-pending" aria-live="polite">
                <span className="svc-ph-thumb">
                  {pending.preview && <img src={pending.preview} alt="" />}
                  <span className="svc-ph-spin"><LogoSpin size={34} decorative /></span>
                </span>
                <div className="svc-ph-body">
                  {pending.of > 1 && <p className="svc-ph-note">Photo {pending.n} of {pending.of}</p>}
                  <p className="svc-ph-note is-empty">{pending.stage === 'prep' ? (pending.heic ? 'Converting from HEIC…' : 'Preparing the photo…') : 'Saving to Smartsheet…'}</p>
                </div>
              </li>
            )}
          </ul>
        )}
        {canWrite && !coarse && q.data && !full && (
          <button type="button" className={`svc-ph-drop${dragging ? ' is-over' : ''}`} disabled={!canAdd} onClick={() => fileRef.current?.click()}>
            <IconUpload size={20} />
            <span className="svc-ph-drop-t"><b>Drag {fromClient ? 'the client’s photos' : 'photos'} here</b> or click to choose them</span>
            <span className="svc-ph-drop-s">JPEG, PNG or HEIC · {plural(room, 'more photo')} can be added</span>
          </button>
        )}
      </div>

      {viewed && createPortal(
        <Modal
          title={`${fromClient ? 'Client photo' : 'Photo'} ${shown + 1} of ${photos.length}`}
          onClose={() => setViewing(null)}
          closeLabel="Close photo"
          width={960}
          className="svc-ph-viewer"
          footer={
            <>
              <a className="btn" href={servicePhotoUrl(rowId, viewed.id)} target="_blank" rel="noopener noreferrer">Open in new tab</a>
              {canWrite && (
                <button type="button" className="btn btn-danger" disabled={deleting(viewed)} onClick={() => void remove(viewed)}>
                  <IconTrash size={15} /> Delete
                </button>
              )}
              <button type="button" className="btn btn-ghost svc-ph-done" onClick={() => setViewing(null)}>Close</button>
            </>
          }
        >
          <figure className="svc-ph-fig">
            <div className="svc-ph-frame">
              <PhotoImage
                key={viewed.id}
                className="svc-ph-big"
                src={servicePhotoUrl(rowId, viewed.id)}
                heic={isHeicPhoto(viewed)}
                maxEdge={1920}
                alt={viewed.note || `Photo ${shown + 1} of ${name}`}
                fallback={(busy) => <span className="svc-ph-nopv svc-ph-nobig">{busy ? <LogoSpin size={32} decorative /> : <IconCamera size={28} />}<span>{busy ? 'Opening the HEIC photo…' : 'This photo can’t be shown here. Open it in a new tab to download it.'}</span></span>}
              />
            </div>
            <figcaption>
              {viewed.note && <span className="svc-ph-cap" lang={lang(viewed.note)}>{viewed.note}</span>}
              <span className="svc-ph-at" lang={lang(who(viewed))}>{[who(viewed), when(viewed)].filter(Boolean).join(' · ')}</span>
            </figcaption>
          </figure>
        </Modal>,
        document.body,
      )}
      {confirmNode && createPortal(confirmNode, document.body)}
    </Modal>,
    document.body,
  );
}

/** a saved photo. One the browser can't show itself (a HEIC outside Safari) is drawn from the file as a
 *  JPEG (lib/heic.ts) at up to `maxEdge` px; meanwhile, or when even that fails, `fallback` shows (told
 *  whether it is still busy). */
function PhotoImage({ src, heic, maxEdge, alt, className, lazy, fallback }: {
  src: string;
  heic: boolean;
  maxEdge: number;
  alt: string;
  className?: string;
  lazy?: boolean;
  fallback: (busy: boolean) => ReactNode;
}) {
  const [state, setState] = useState<{ url: string } | 'native' | 'busy' | 'failed'>('native');
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);
  const onError = () => {
    if (!heic || state !== 'native') { setState('failed'); return; }
    setState('busy');
    heicPreviewUrl(src, maxEdge).then(
      (url) => { if (live.current) setState({ url }); },
      () => { if (live.current) setState('failed'); },
    );
  };
  if (state === 'busy' || state === 'failed') return <>{fallback(state === 'busy')}</>;
  const url = state === 'native' ? src : state.url;
  return <img className={className} src={url} alt={alt} loading={lazy ? 'lazy' : undefined} decoding="async" onError={onError} />;
}

/** a photo's note: edits locally, saves on blur when it changed. Esc leaves the field (and saves) rather
 *  than closing the dialog under a half-typed note. */
function NoteField({ rowId, photo, label }: { rowId: number; photo: ServicePhoto; label: string }) {
  const edit = useEditServicePhotoNote();
  const [draft, setDraft] = useState(photo.note);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const editing = useRef(false);
  // follow the server's note (a refetch, another editor) unless this field is being typed in
  useEffect(() => { if (!editing.current) setDraft(photo.note); }, [photo.note]);
  // the phone's back gesture closes the dialog without a blur: save a half-typed note on the way out
  const latest = useRef({ draft, note: photo.note, commentId: photo.commentId });
  latest.current = { draft, note: photo.note, commentId: photo.commentId };
  const { mutate } = edit;
  useEffect(() => () => {
    const l = latest.current;
    if (editing.current && l.commentId != null && l.draft.trim() !== l.note.trim()) mutate({ rowId, commentId: l.commentId, note: l.draft.trim() });
  }, [mutate, rowId]);
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  const save = () => {
    editing.current = false;
    const next = draft.trim();
    if (next === photo.note.trim() || photo.commentId == null) { setDraft(photo.note); return; }
    setError(null);
    edit.mutate(
      { rowId, commentId: photo.commentId, note: next },
      { onSuccess: () => { setDraft(next); setSaved(true); }, onError: (e) => setError(e) },
    );
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur(); }
  };
  return (
    <div className="svc-ph-notewrap">
      <textarea
        className="field svc-ph-notein"
        value={draft}
        rows={2}
        maxLength={SERVICE_PHOTO_NOTE_MAX}
        placeholder="Add a note…"
        aria-label={label}
        lang={lang(draft)}
        onFocus={() => { editing.current = true; setSaved(false); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={onKey}
      />
      <span className="svc-ph-notest" aria-live="polite">{edit.isPending ? 'Saving…' : saved ? 'Saved' : ''}</span>
      {error != null && <InlineError error={error} onDismiss={() => setError(null)} />}
    </div>
  );
}
